<template>
  <div class="customer-assign-provider-page">
    <div class="mb-2">
      <h3>{{ t('Assign Service Provider') }}</h3>
      <p class="text">{{ t('Assign Service Provider Desc') }}</p>
    </div>

    <div v-if="isLoading" class="text-center py-5">
      <b-spinner small type="grow" variant="primary" />
      <div class="mt-1">{{ t('Loading') }}...</div>
    </div>

    <div v-else class="mx-5">
      <b-card
        v-for="(item, index) in rows"
        :key="index"
        no-body
        :class="item.is_allowed ? 'border-primary' : 'border'"
        class="mb-2"
      >
        <b-card-body>
          <div class="d-flex justify-content-between align-items-center flex-wrap">
            <div class="d-flex align-items-center mb-1 mb-md-0">
              <div class="mr-1">
                <img
                  :src="getProviderLogo(item.provider_code || item.provider)"
                  :alt="item.name"
                  style="max-width: 50px; max-height: 50px; object-fit: contain"
                />
              </div>
              <div>
                <h5 class="mb-0 font-weight-bolder">{{ item.name }}</h5>
                <b-badge
                  :variant="item.is_allowed ? 'light-success' : 'light-secondary'"
                  pill
                  class="mt-50"
                >
                  {{ t(item.is_allowed ? 'Active' : 'Passive') }}
                </b-badge>
              </div>
            </div>

            <div class="d-flex align-items-center">
              <span
                class="mr-1 text-muted small text-right d-none d-md-block"
                style="max-width: 200px; line-height: 1.2"
              >
                {{ t('Provider Allowed Toggle') }}
              </span>
              <b-form-checkbox v-model="item.is_allowed" switch size="lg" />
            </div>
          </div>

          <b-collapse :visible="item.is_allowed" class="mt-2">
            <hr />
            <div class="mb-1">
              <span class="font-weight-bold">{{ t('Redirect Options') }}</span>
              <feather-icon
                :id="`redirect-info-${index}`"
                icon="InfoIcon"
                size="14"
                class="text-muted ml-50"
              />
              <b-tooltip
                :target="`redirect-info-${index}`"
                triggers="hover"
                :title="t('Redirect Info Tooltip')"
              />
            </div>

            <div class="redirect-options-list">
              <div
                v-for="option in dispatchOptionsWithMeta"
                :key="option.value"
                class="p-1 mb-1 border rounded cursor-pointer transition-all provider-option-item"
                :class="
                  item.dispatch_mode === option.value
                    ? 'bg-light-primary border-primary selected-provider-option'
                    : 'unselected-provider-option'
                "
                @click="item.dispatch_mode = option.value"
              >
                <div class="d-flex align-items-start">
                  <b-form-radio
                    v-model="item.dispatch_mode"
                    :value="option.value"
                    class="mt-25 mr-1"
                  />
                  <div>
                    <div class="font-weight-bold mb-25 d-flex align-items-center">
                      <feather-icon :icon="option.icon" size="16" class="mr-50" />
                      {{ option.title }}
                    </div>
                    <small class="text-muted" style="display: block; line-height: 1.4">{{
                      option.desc
                    }}</small>
                  </div>
                </div>
              </div>
            </div>

            <div class="border-top mt-2 small">
              <div v-if="item.dispatch_mode" class="mt-1 d-flex align-items-center">
                <feather-icon icon="BoxIcon" size="14" class="mr-50 bg-light-primary" />
                <span
                  >{{ t('Shipment Flow') }}:
                  <b>{{ getSelectedOptionTitle(item.dispatch_mode) }}</b></span
                >
              </div>
            </div>
          </b-collapse>
        </b-card-body>
      </b-card>

      <div class="text-right mt-3 pb-3">
        <b-button variant="primary" @click="submit" :disabled="submitting">
          <b-spinner v-if="submitting" small class="mr-50" />
          {{ t('Save') }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BButton,
  BSpinner,
  BFormCheckbox,
  BBadge,
  BCollapse,
  BFormRadio,
  BTooltip
} from 'bootstrap-vue';
import useService from '@/auth/service/useService';
import { useUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper.js';
import { getProviderLogo } from '@/libs/providerHelper';

export default {
  components: {
    BCard,
    BCardBody,
    BButton,
    BSpinner,
    BFormCheckbox,
    BBadge,
    BCollapse,
    BFormRadio,
    BTooltip
  },
  mixins: [helper],
  setup() {
    const { t } = useUtils();
    return { t, getProviderLogo };
  },
  data: () => ({ isLoading: false, submitting: false, rows: [], dispatchMode: [] }),
  computed: {
    dispatchOptionsWithMeta() {
      const defs = {
        WITH_DIRECT_PROVIDER: {
          icon: 'PackageIcon',
          title: 'Redirect to Service Provider',
          desc: 'Redirect to Service Provider Desc'
        },
        WITH_DEPARTMENT_REDIRECT: {
          icon: 'HomeIcon',
          title: 'Redirect via Warehouse',
          desc: 'Redirect via Warehouse Desc'
        },
        ASYNC_PROVIDER: {
          icon: 'ClockIcon',
          title: 'Send Later Async',
          desc: 'Send Later Async Desc'
        },
        default: { icon: 'ArrowRightCircleIcon', title: '', desc: '' }
      };

      return (this.dispatchMode || []).map(opt => {
        const meta = defs[opt.key] || defs.default;
        return {
          value: opt.value,
          icon: meta.icon,
          title: meta === defs.default ? opt.info : this.t(meta.title),
          desc: meta === defs.default ? '' : this.t(meta.desc)
        };
      });
    }
  },
  async created() {
    this.dispatchMode = await this.getDispatchOptions();
    this.loadItems();
  },
  methods: {
    loadItems() {
      this.isLoading = true;
      useService
        .getCustomerAssigneProvider(this.$route.params.id)
        .then(({ data }) => {
          this.rows = data;
        })
        .catch(e => this.showErrorMessage(e.message || this.t('An error occurred')))
        .finally(() => {
          this.isLoading = false;
        });
    },
    getSelectedOptionTitle(val) {
      return (this.dispatchOptionsWithMeta.find(o => o.value === val) || {}).title || '-';
    },
    submit() {
      this.submitting = true;
      const providers = this.rows
        .filter(i => i.is_allowed)
        .map(i => ({ provider: i.provider, dispatch_mode: i.dispatch_mode }));

      useService
        .updateCustomerAssigneProvider(this.$route.params.id, { providers })
        .then(res => this.showSuccessMessage(res.message))
        .catch(err => {
          const msgs = Array.isArray(err)
            ? err
            : [{ message: err.message || this.t('An error occurred') }];
          msgs.forEach(m => this.showErrorMessage(m.message));
        })
        .finally(() => {
          this.submitting = false;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.transition-all {
  transition: all 0.2s ease-in-out;
}
.cursor-pointer {
  cursor: pointer;
}
</style>

<style lang="scss">
.unselected-provider-option {
  background-color: #f8f9fa;
}
.customer-assign-provider-page
  .selected-provider-option
  .custom-control-input:checked
  ~ .custom-control-label {
  &::before {
    background-color: #fff;
    border-color: #0ca5cd;
    box-shadow: none;
    background-image: radial-gradient(circle, #0ca5cd 40%, transparent 42%);
    background-repeat: no-repeat;
    background-position: center;
    background-size: 100% 100%;
  }
}

.dark-layout .customer-assign-provider-page {
  .unselected-provider-option {
    background-color: #283046;
    border-color: #3b4253;
  }
  .selected-provider-option {
    background-color: #fff;
    border-color: #fff;
    .font-weight-bold,
    .feather-icon {
      color: #05a6ce;
    }
    .text-muted {
      color: #05a6ce;
    }
  }
}
</style>
